import { z } from 'zod';

export const CUSTOMER_NOT_FOUND_ERROR = 'Customer not found.';
export const CUSTOMER_LOAD_ERROR = 'Could not load customers. Try again or check the logs.';
export const CUSTOMER_SAVE_ERROR = 'Could not save customer. Try again or check the logs.';
export const CUSTOMER_DELETE_ERROR = 'Could not delete customer. Try again or check the logs.';
export const NAME_REQUIRED_ERROR = 'Name required.';
export const INVALID_EMAIL_ERROR = 'Enter a valid email address.';

export const COUNTERPARTY_NAME_MAX_LENGTH = 200;
export const COUNTERPARTY_EMAIL_MAX_LENGTH = 254;
export const COUNTERPARTY_BLOCK_MAX_LENGTH = 4000;

const RequiredNameSchema = z
  .string()
  .trim()
  .min(1, NAME_REQUIRED_ERROR)
  .max(COUNTERPARTY_NAME_MAX_LENGTH);

const OptionalEmailInputSchema = optionalTrimmedString()
  .refine((value) => value === null || value.length <= COUNTERPARTY_EMAIL_MAX_LENGTH, {
    message: `Email must contain at most ${COUNTERPARTY_EMAIL_MAX_LENGTH} characters.`,
  })
  .refine((value) => value === null || z.email().safeParse(value).success, {
    message: INVALID_EMAIL_ERROR,
  });

const OptionalBlockInputSchema = optionalTrimmedString().refine(
  (value) => value === null || value.length <= COUNTERPARTY_BLOCK_MAX_LENGTH,
  {
    message: `Must contain at most ${COUNTERPARTY_BLOCK_MAX_LENGTH} characters.`,
  },
);

export const UpsertCounterpartyInputSchema = z
  .object({
    name: RequiredNameSchema,
    email: OptionalEmailInputSchema,
    billTo: OptionalBlockInputSchema,
    shipTo: OptionalBlockInputSchema,
  })
  .strict();

export const CounterpartySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).max(COUNTERPARTY_NAME_MAX_LENGTH),
    email: z.string().max(COUNTERPARTY_EMAIL_MAX_LENGTH).email().nullable(),
    billTo: z.string().max(COUNTERPARTY_BLOCK_MAX_LENGTH).nullable(),
    shipTo: z.string().max(COUNTERPARTY_BLOCK_MAX_LENGTH).nullable(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict();

export const CounterpartiesListResponseSchema = z
  .object({
    counterparties: z.array(CounterpartySchema),
  })
  .strict();

export const CounterpartyResponseSchema = z
  .object({
    counterparty: CounterpartySchema,
  })
  .strict();

export const DeleteCounterpartyResponseSchema = z.object({ ok: z.literal(true) }).strict();

export type Counterparty = z.infer<typeof CounterpartySchema>;
export type UpsertCounterpartyInput = z.infer<typeof UpsertCounterpartyInputSchema>;
export type CounterpartiesListResponse = z.infer<typeof CounterpartiesListResponseSchema>;
export type CounterpartyResponse = z.infer<typeof CounterpartyResponseSchema>;
export type DeleteCounterpartyResponse = z.infer<typeof DeleteCounterpartyResponseSchema>;

function optionalTrimmedString() {
  return z
    .string()
    .nullable()
    .optional()
    .transform((value) => {
      if (value == null) return null;
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    });
}
